import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import './ModuleList.css';
import { useLanguage } from '../contexts/LanguageContext';
import { supabase } from '../supabaseClient';
import Skeleton from './Skeleton';
import { isTextSafe } from '../utils/safeForAds';

const ModuleList = ({ modules, deleteModule, isEditMode = false, onExitEditMode, onSaveSuccess }) => {
  const { t, isRTL } = useLanguage();
  const [editedNames, setEditedNames] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Initialize edited names when entering edit mode
  useEffect(() => {
    if (isEditMode) {
      const initialNames = {};
      modules.forEach(mod => {
        if (mod.id) {
          initialNames[mod.id] = mod.name || '';
        }
      });
      setEditedNames(initialNames);
      setError('');
    }
  }, [isEditMode, modules]);

  if (!Array.isArray(modules)) {
    // Show skeletons or loading state
    return (
      <div className="module-grid">
        {[...Array(3)].map((_, i) => (
          <div key={i} className="card module-card">
            <div className="module-card-content">
              <Skeleton width="60%" height="1.5em" style={{ marginBottom: 12 }} />
              <Skeleton width="80%" height="1em" style={{ marginBottom: 8 }} />
              <Skeleton width="40%" height="0.8em" />
            </div>
            <div className="module-card-actions" style={{ display: 'flex', gap: 8 }}>
              <Skeleton width="60px" height="2.2em" />
              <Skeleton width="60px" height="2.2em" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  const handleDelete = (moduleId, moduleName) => {
    const displayName = moduleName || t('thisModule') || 'this module';
    if (window.confirm(`${t('areYouSureDelete')} "${displayName}"?`)) {
      deleteModule(moduleId);
    }
  };

  const handleNameChange = (moduleId, newName) => {
    if (newName.length > 150) {
      setError(t('moduleNameMaxLength') || 'Module name cannot exceed 150 characters');
      return;
    }
    setError('');
    setEditedNames(prev => ({
      ...prev,
      [moduleId]: newName.substring(0, 150)
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const userId = session?.user?.id;
      
      if (!userId) {
        setError(t('pleaseLogIn') || 'Please log in');
        setSaving(false);
        return;
      }

      // Get all modules that have been changed
      const updates = [];
      for (const [moduleId, newName] of Object.entries(editedNames)) {
        const module = modules.find(m => m.id === moduleId);
        if (module && module.name !== newName.trim()) {
          // Check for profanity in module name
          if (!isTextSafe(newName.trim())) {
            setError(t('inappropriateContentDetected') || 'Inappropriate content detected. Please use appropriate language.');
            setSaving(false);
            return;
          }
          updates.push({
            id: moduleId,
            name: newName.trim().substring(0, 150)
          });
        }
      }

      if (updates.length === 0) {
        // No changes to save
        onExitEditMode();
        setSaving(false);
        return;
      }

      // Update all modules in parallel
      const updatePromises = updates.map(update =>
        supabase
          .from('modules_new')
          .update({ name: update.name, updated_at: new Date().toISOString() })
          .eq('id', update.id)
          .eq('user_id', userId)
      );

      const results = await Promise.all(updatePromises);
      
      // Check for errors
      const hasError = results.some(result => result.error);
      if (hasError) {
        setError(t('errorUpdatingModuleNames') || 'Error updating module names. Please try again.');
        setSaving(false);
        return;
      }

      // Success - refresh and exit edit mode
      if (onSaveSuccess) {
        onSaveSuccess();
      }
    } catch (err) {
      console.error('Error saving module names:', err);
      setError(t('errorUpdatingModuleNames') || 'Error updating module names. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // Get total flashcards from module's num_words column (from Supabase)
  const getTotalFlashcards = (module) => {
    return module.num_words || 0;
  };

  // Sort modules by position (descending - highest position first)
  const sortedModules = [...modules].sort((a, b) => {
    const posA = a.position ?? 0;
    const posB = b.position ?? 0;
    return posB - posA; // Descending order (highest position first)
  });

  return (
    <>
      {error && (
        <div className="error-message" style={{ marginBottom: '1rem', textAlign: 'center' }}>
          {error}
        </div>
      )}
      {isEditMode && sortedModules.length > 0 && (
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          marginBottom: '1.5rem'
        }}>
          <button
            onClick={handleSave}
            className="btn btn-primary"
            disabled={saving}
            style={{ minWidth: '120px' }}
          >
            {saving ? t('saving') || 'Saving...' : t('saveChanges') || 'Save Changes'}
          </button>
        </div>
      )}
    <div className="module-grid">
      {sortedModules.length === 0 ? (
        <p className="module-list-empty">{t('noModulesYet')}</p>
      ) : (
        sortedModules.map((mod) => {
          const totalFlashcards = getTotalFlashcards(mod);
          const wordGroupCount = mod.num_groups || 0;
          const currentName = editedNames[mod.id] !== undefined ? editedNames[mod.id] : (mod.name || '');
          const moduleTypeLabel = mod.from_lang === mod.to_lang
            ? t('definitions')
            : `${t(mod.from_lang)} ${isRTL ? '←' : '→'} ${t(mod.to_lang)}`;

          return (
          <div key={mod.id} className="card module-card">
            <div className="module-card-content">
                {isEditMode ? (
                  <>
                    <input
                      type="text"
                      value={currentName}
                      onChange={(e) => handleNameChange(mod.id, e.target.value)}
                      placeholder={t('moduleNamePlaceholder') || 'e.g., Spanish Basics, French Conversation'}
                      maxLength={150}
                      className="module-card-title"
                      style={{
                        width: '100%',
                        padding: '8px 12px',
                        border: '2px solid #386641',
                        borderRadius: '6px',
                        background: '#ffffff',
                        fontSize: '1.25rem',
                        fontWeight: 600,
                        margin: '0 0 0.5rem 0',
                        fontFamily: 'inherit',
                        color: '#333',
                        outline: 'none',
                        cursor: 'text',
                        boxSizing: 'border-box'
                      }}
                    />
                    <p className="module-card-topic">{mod.name ? moduleTypeLabel : (mod.word_groups && mod.word_groups.length > 0 ? mod.word_groups[0].name : '')}</p>
                  </>
                ) : (
                  <>
                    <h3 className="module-card-title">{mod.name || moduleTypeLabel}</h3>
                    <p className="module-card-topic">{mod.name ? moduleTypeLabel : (mod.word_groups && mod.word_groups.length > 0 ? mod.word_groups[0].name : '')}</p>
                  </>
                )}
              <div className="module-card-meta">
                <span>
                    {wordGroupCount > 0
                      ? `${wordGroupCount} ${t('wordGroup', wordGroupCount)} • ${totalFlashcards} ${t('flashcard', totalFlashcards)}`
                    : ''}
                </span>
                <span style={{ marginLeft: '1rem', color: '#888', fontSize: '0.95em' }}>
                  {mod.created_at ? `${t('created')} ${new Date(mod.created_at).toLocaleString(undefined, {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}` : ''}
                </span>
              </div>
            </div>
            <div className="module-card-actions">
              <Link to={`/module/${mod.id}`} className="btn btn-primary" state={{ module: mod }}>{t('resume')}</Link>
              <button 
                  onClick={() => handleDelete(mod.id, mod.name || `${t(mod.from_lang)} ${isRTL ? '←' : '→'} ${t(mod.to_lang)}`)} 
                className="btn btn-danger"
                  aria-label={`${t('delete')} ${mod.name || `${t(mod.from_lang)} ${isRTL ? '←' : '→'} ${t(mod.to_lang)}`}`}
              >
                {t('delete')}
              </button>
            </div>
          </div>
          );
        })
      )}
    </div>
    </>
  );
};

export default ModuleList;
